import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  Cell,
} from 'recharts';
import { Refresh, TrendingUp, ReceiptLong, LocalShipping, WarningAmber } from '@mui/icons-material';
import { PageHeader, StatCard, TableEmptyState } from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { procurementService, ProcurementAnalytics } from '../../services/procurementService';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const fmtMonth = (m: string): string => {
  const match = /^(\d{4})-(\d{2})$/.exec(m || '');
  if (!match) return m;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

const fmtCompact = (v: number): string => formatIndianNumber(v);
const fmtPct = (v: number | null | undefined): string => (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);

const EXCEPTION_LABEL: Record<string, string> = {
  NO_PO: 'No PO Link',
  NO_GRN: 'No GRN',
  QTY_OVER_RECEIPT: 'Qty Over-Receipt',
  QTY_UNDER_RECEIPT: 'Qty Under-Receipt',
  PRICE_VARIANCE: 'Price Variance',
  VALUE_VARIANCE: 'Value Variance',
  DUPLICATE_INVOICE: 'Duplicate Invoice',
  TAX_MISMATCH: 'Tax Mismatch',
  UOM_MISMATCH: 'UOM Mismatch',
  VENDOR_MISMATCH: 'Vendor Mismatch',
};

// ---------------------------------------------------------------------------
// Section shell — a bordered card matching pageKit's TableSurface treatment,
// with a title/subtitle header. Not lifted into pageKit itself since no other
// page yet needs a titled chart card; if a third one shows up, promote it.
// ---------------------------------------------------------------------------

const SectionCard: React.FC<{ title: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode; sx?: any }> = ({
  title,
  subtitle,
  action,
  children,
  sx,
}) => (
  <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, height: '100%', ...sx }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: subtitle ? 0.25 : 1.5, gap: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {action}
    </Box>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

/** A single labelled horizontal segment bar — used for match rate and on-time delivery. */
const SegmentBar: React.FC<{ segments: { label: string; value: number; color: string }[] }> = ({ segments }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) {
    return <Typography variant="caption" color="text.secondary">No data yet.</Typography>;
  }
  return (
    <Box>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', mb: 1.25 }}>
        {segments
          .filter((seg) => seg.value > 0)
          .map((seg) => (
            <Tooltip key={seg.label} title={`${seg.label}: ${seg.value}`}>
              <Box sx={{ width: `${(seg.value / total) * 100}%`, backgroundColor: seg.color, transition: 'width .4s cubic-bezier(.16,1,.3,1)' }} />
            </Tooltip>
          ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {segments.map((seg) => (
          <Box key={seg.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: seg.color, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              {seg.label} <strong style={{ color: 'inherit' }}>{seg.value}</strong>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const ProcurementAnalyticsPage: React.FC = () => {
  const t = useChartTokens();
  const [data, setData] = useState<ProcurementAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = () => {
    setLoading(true);
    setError(null);
    procurementService
      .getProcurementAnalytics()
      .then(setData)
      .catch((err: any) => setError(err.message || 'Failed to load procurement analytics'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spendByMonth = (data?.spendByMonth || []).slice(-12).map((r) => ({ ...r, label: fmtMonth(r.month) }));
  const spendByCategory = data?.spendByCategory || [];
  const spendBySupplier = (data?.spendBySupplier || []).slice(0, 10);
  const poAging = data?.poAging || [];
  const topExceptions = data?.topExceptions || [];
  const matchRate = data?.matchRate;
  const onTime = data?.onTimeDelivery;
  const cycleTimes = data?.cycleTimes;
  const conc = data?.supplierConcentration;

  return (
    <Box>
      <PageHeader
        title="Procurement Analytics"
        subtitle="Spend, supplier concentration, cycle times and match quality across the buy-to-pay cycle"
        actions={
          <IconButton onClick={fetchData} disabled={loading}>
            <Refresh />
          </IconButton>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !data ? (
        <TableEmptyState title="Could not load procurement analytics" hint="Try refreshing." />
      ) : (
        <Grid container spacing={2.5}>
          {/* Spend Trend */}
          <Grid size={{ xs: 12, md: 8 }}>
            <SectionCard title="Spend Trend" subtitle="Last 12 months, PO + invoice spend">
              {spendByMonth.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No spend recorded yet.</Typography>
              ) : (
                <Box sx={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={spendByMonth} margin={{ top: 8, right: 8, left: -6, bottom: 0 }}>
                      <CartesianGrid {...gridProps(t)} />
                      <XAxis dataKey="label" {...axisProps(t)} minTickGap={24} />
                      <YAxis {...axisProps(t)} width={64} tickFormatter={(v: number) => fmtCompact(v)} />
                      <RTooltip {...tooltipStyle(t)} formatter={(v: number) => [formatIndianNumber(v), 'Spend']} />
                      <Line type="monotone" dataKey="spend" stroke={t.series(0)} strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* Cycle Times */}
          <Grid size={{ xs: 12, md: 4 }}>
            <SectionCard title="Cycle Times" subtitle="Average days, buy-to-pay">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <StatCard title="PR → PO" value={cycleTimes ? `${cycleTimes.prToPoDays.toFixed(1)}d` : null} icon={<TrendingUp />} color={t.categorical[0]} />
                <StatCard title="PO → GRN" value={cycleTimes ? `${cycleTimes.poToGrnDays.toFixed(1)}d` : null} icon={<LocalShipping />} color={t.categorical[1]} />
                <StatCard title="GRN → Invoice" value={cycleTimes ? `${cycleTimes.grnToInvoiceDays.toFixed(1)}d` : null} icon={<ReceiptLong />} color={t.categorical[2]} />
              </Box>
            </SectionCard>
          </Grid>

          {/* Spend by Category */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Spend by Category" subtitle="Share of total spend, current FY">
              {spendByCategory.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No category data yet.</Typography>
              ) : (
                <Box sx={{ height: Math.max(180, spendByCategory.length * 34) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={spendByCategory} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
                      <CartesianGrid {...gridProps(t)} horizontal={false} />
                      <XAxis type="number" {...axisProps(t)} tickFormatter={(v: number) => fmtCompact(v)} />
                      <YAxis type="category" dataKey="category" {...axisProps(t)} width={120} tick={{ fill: t.textSecondary, fontSize: 11 }} />
                      <RTooltip
                        {...tooltipStyle(t)}
                        formatter={(v: number, _n, p: any) => [`${formatIndianNumber(v)} (${p?.payload?.share != null ? p.payload.share.toFixed(1) : '—'}%)`, 'Spend']}
                      />
                      <Bar dataKey="spend" radius={[0, 3, 3, 0]} maxBarSize={20}>
                        {spendByCategory.map((_, i) => (
                          <Cell key={i} fill={t.series(i)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* Top Suppliers + Concentration */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Top Suppliers" subtitle="Spend concentration — how reliant procurement is on a few vendors">
              <Grid container spacing={1.25} sx={{ mb: 2 }}>
                <Grid size={4}>
                  <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(conc?.top1Pct)}</Typography>
                    <Typography variant="caption" color="text.secondary">Top 1</Typography>
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(conc?.top3Pct)}</Typography>
                    <Typography variant="caption" color="text.secondary">Top 3</Typography>
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(conc?.top5Pct)}</Typography>
                    <Typography variant="caption" color="text.secondary">Top 5</Typography>
                  </Box>
                </Grid>
              </Grid>
              {spendBySupplier.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No supplier spend recorded yet.</Typography>
              ) : (
                <Box sx={{ height: Math.max(180, spendBySupplier.length * 30) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={spendBySupplier} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
                      <CartesianGrid {...gridProps(t)} horizontal={false} />
                      <XAxis type="number" {...axisProps(t)} tickFormatter={(v: number) => fmtCompact(v)} />
                      <YAxis type="category" dataKey="name" {...axisProps(t)} width={130} tick={{ fill: t.textSecondary, fontSize: 11 }} />
                      <RTooltip
                        {...tooltipStyle(t)}
                        formatter={(v: number, _n, p: any) => [`${formatIndianNumber(v)} (${p?.payload?.share != null ? p.payload.share.toFixed(1) : '—'}%)`, 'Spend']}
                      />
                      <Bar dataKey="spend" fill={t.series(0)} radius={[0, 3, 3, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* PO Aging */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="PO Aging" subtitle="Open purchase orders by age bucket">
              {poAging.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No open purchase orders.</Typography>
              ) : (
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={poAging} margin={{ top: 12, right: 8, left: -6, bottom: 4 }}>
                      <CartesianGrid {...gridProps(t)} vertical={false} />
                      <XAxis dataKey="bucket" {...axisProps(t)} />
                      <YAxis {...axisProps(t)} width={64} tickFormatter={(v: number) => fmtCompact(v)} />
                      <RTooltip
                        {...tooltipStyle(t)}
                        formatter={(v: number, _n, p: any) => [`${formatIndianNumber(v)} · ${p?.payload?.count ?? 0} PO(s)`, 'Value']}
                      />
                      <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={40}>
                        {poAging.map((_, i) => (
                          <Cell key={i} fill={i >= poAging.length - 2 ? t.status.critical : t.series(i)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* Match Rate */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Three-Way Match Quality" subtitle="Invoices reconciled clean vs held by exception">
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: t.status.good, fontVariantNumeric: 'tabular-nums' }}>
                  {matchRate ? fmtPct(matchRate.cleanPct) : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">clean match rate ({matchRate?.total ?? 0} invoices)</Typography>
              </Box>
              {matchRate && (
                <SegmentBar
                  segments={[
                    { label: 'Matched', value: matchRate.matched, color: t.status.good },
                    { label: 'Within Tolerance', value: matchRate.withinTolerance, color: t.status.warning },
                    { label: 'Exception', value: matchRate.exception, color: t.status.critical },
                    { label: 'Unmatched', value: matchRate.unmatched, color: t.axis },
                  ]}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Invoice exception rate: <strong>{fmtPct(data.invoiceExceptionRate)}</strong>
              </Typography>
            </SectionCard>
          </Grid>

          {/* On-Time Delivery */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="On-Time Delivery" subtitle="Goods receipts vs the PO-promised delivery date">
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: onTime && onTime.pct >= 80 ? t.status.good : t.status.warning, fontVariantNumeric: 'tabular-nums' }}>
                  {onTime ? fmtPct(onTime.pct) : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">on-time rate</Typography>
              </Box>
              {onTime && (
                <SegmentBar
                  segments={[
                    { label: 'On Time', value: onTime.onTime, color: t.status.good },
                    { label: 'Late', value: onTime.late, color: t.status.critical },
                  ]}
                />
              )}
            </SectionCard>
          </Grid>

          {/* Top Exceptions */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Top Exceptions"
              subtitle="Most frequent three-way match findings"
              action={<WarningAmber sx={{ fontSize: 18, color: t.status.warning }} />}
            >
              {topExceptions.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No exceptions raised — matching is clean.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(() => {
                    const max = Math.max(...topExceptions.map((e) => e.count), 1);
                    return topExceptions.map((e, i) => (
                      <Box key={e.type} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Typography variant="body2" sx={{ minWidth: 150, flexShrink: 0 }}>
                          {EXCEPTION_LABEL[e.type] || e.type}
                        </Typography>
                        <Box sx={{ flexGrow: 1, height: 8, borderRadius: 4, backgroundColor: `${t.series(i)}1f`, overflow: 'hidden' }}>
                          <Box sx={{ width: `${(e.count / max) * 100}%`, height: '100%', backgroundColor: t.series(i), borderRadius: 4 }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 24, textAlign: 'right' }}>{e.count}</Typography>
                      </Box>
                    ));
                  })()}
                </Box>
              )}
            </SectionCard>
          </Grid>

        </Grid>
      )}
    </Box>
  );
};

export default ProcurementAnalyticsPage;
